import React from 'react';
import { routes } from 'constants/config';
import AlertBox, { Heading } from 'components/shared/AlertBox';
import { Button, ButtonGroup, Spinner, Card } from 'ui';
import request from 'utils/request_ajax';
import {
  WebcamRecordingQuestion,
  AudioRecordingQuestion,
  ScreenRecordingQuestion,
  WrittenResponseQuestion,
} from 'components/Questions';
import QuestionPrompt from 'components/Practice/Learner/QuestionPrompt';
import LearnerConversation from 'components/Practice/Learner/LearnerConversation';
import { FeatureContext } from './FeatureContext';
import QuestionPanel from './QuestionPanel';

const SCENARIO_TYPES = {
  WEBCAM_RECORDING: 'webcam_recording',
  AUDIO_RECORDING: 'audio_recording',
  SCREEN_RECORDING: 'screen_recording',
  WRITTEN_RESPONSE: 'written_response',
  CONVERSATION: 'conversation',
};

class CurrentResponseTab extends React.Component {
  static contextType = FeatureContext;

  state = {
    scenario: null,
    loading: true,
    loadError: false,
    saveError: false,
    isComplete: false,
    isConversation: null,
    answer: null,
    practiceAttemptResponses: null,
    provider: null,
  };

  componentDidMount() {
    const url = this.props.isPreviewing
      ? routes.ON_DEMAND_PRACTICE.PREVIEW.SHOW(this.props.scenarioId)
      : routes.ON_DEMAND_PRACTICE.LEARNING.SHOW(this.props.scenarioId);
    request
      .get(url)
      .then(scenario => {
        if (scenario) {
          this.setState({
            loading: false,
            scenario,
            loadError: false,
            isConversation:
              scenario.scenarioType === SCENARIO_TYPES.CONVERSATION,
          });
        } else {
          this.setState({
            loading: false,
            loadError: true,
          });
        }
      })
      .fail(() => {
        this.setState({
          loading: false,
          loadError: true,
        });
      });
  }

  closeSaveErrorModal = () => {
    this.setState({
      saveError: false,
      saving: false,
    });
  };

  showLatestAttempt = () => {
    this.props.setTabState({
      tab: 1,
      highlightFirstAttempt: true,
      saveError: false,
      saving: false,
      disabled: false,
    });
  };

  postAttempt = () => {
    const { answer, practiceAttemptResponses, scenario, provider } = this.state;
    const attempt = {
      answer,
      questionInteractionResponseAttemptsAttributes: practiceAttemptResponses,
      scenario_prompt: scenario.prompt,
    };

    if (provider) attempt.provider = provider;
    return request.postJSON(
      routes.ON_DEMAND_PRACTICE.LEARNING.ATTEMPTS.INDEX(this.props.scenarioId),
      attempt,
    );
  };

  saveAttemptWithoutFeedback = () => {
    this.postAttempt()
      .then(data => {
        this.setState({ saving: false });
        if (data.success) {
          this.showLatestAttempt();
        } else {
          this.setState({ saveError: true });
        }
      })
      .fail(() => {
        this.setState({ saveError: true });
      });
  };

  saveAttempt = answer => {
    this.setState({ saving: true });
    if (typeof answer === 'string') {
      this.setState({ answer }, () => {
        this.saveAttemptWithoutFeedback();
      });
    } else {
      this.saveAttemptWithoutFeedback();
    }
  };

  saveAttemptWithResponses = practiceAttemptResponses => {
    this.setState({ saving: true, practiceAttemptResponses }, () => {
      this.postAttempt()
        .then(data => {
          this.setState({ saving: false });
          if (!data.success) {
            this.setState({ saveError: true });
          }
        })
        .fail(() => {
          this.setState({ saveError: true });
        });
    });
  };

  saveAttemptWithFeedback = () => {
    this.setState({ saving: true });
    this.postAttempt()
      .then(data => {
        if (data.success) {
          window.location.href =
            routes.ON_DEMAND_PRACTICE.LEARNING.ATTEMPTS.REQUEST_FEEDBACK(
              data.scenario_id,
              data.attempt_id,
            );
        } else {
          this.setState({ saveError: true });
        }
      })
      .fail(() => {
        this.setState({ saveError: true });
      });
  };

  setAnswer = (answer, provider) => {
    this.setState({ answer, isComplete: true, provider });
  };

  resetAnswer = () => {
    this.setState({ answer: null, isComplete: false, provider: null });
  };

  renderQuestion() {
    const { scenario } = this.state;
    if (scenario.scenarioType === SCENARIO_TYPES.WEBCAM_RECORDING) {
      return (
        <WebcamRecordingQuestion
          id={scenario.id}
          prompt={scenario.prompt}
          apiKey={this.props.ziggeoApiKey}
          handleSave={this.saveAttempt}
          setAnswer={this.setAnswer}
          resetAnswer={this.resetAnswer}
          hasZiggeoForAllMedia={this.props.hasZiggeoForAllMedia}
        />
      );
    }
    if (scenario.scenarioType === SCENARIO_TYPES.AUDIO_RECORDING) {
      return (
        <AudioRecordingQuestion
          questionId={scenario.id}
          prompt={scenario.prompt}
          hippoPreviewBase={this.props.hippoPreviewBase}
          handleSave={this.saveAttempt}
          currentUserId={this.props.currentUserId}
          setAnswer={this.setAnswer}
          resetAnswer={this.resetAnswer}
          isPreviewing={this.props.isPreviewing}
        />
      );
    }
    if (scenario.scenarioType === SCENARIO_TYPES.SCREEN_RECORDING) {
      return (
        <ScreenRecordingQuestion
          questionId={scenario.id}
          prompt={scenario.prompt}
          hippoPreviewBase={this.props.hippoPreviewBase}
          handleSave={this.saveAttempt}
          currentUserId={this.props.currentUserId}
          setAnswer={this.setAnswer}
          resetAnswer={this.resetAnswer}
          apiKey={this.props.ziggeoApiKey}
        />
      );
    }
    if (scenario.scenarioType === SCENARIO_TYPES.WRITTEN_RESPONSE) {
      return (
        <WrittenResponseQuestion
          questionId={scenario.id}
          handleSave={this.saveAttempt}
          setAnswer={this.setAnswer}
          resetAnswer={this.resetAnswer}
          currentUserId={this.props.currentUserId}
        />
      );
    }
    return <div />;
  }

  render() {
    const {
      loading,
      loadError,
      saveError,
      scenario,
      isComplete,
      isConversation,
      saving,
      answer,
    } = this.state;
    return (
      <div>
        {isConversation && (
          <div className="OnDemandPracticeConversation-instructions">
            <Card>
              <Card.Content>
                <h3>
                  {t(
                    'practice.on_demand_practice.conversation.instruction_title',
                  )}
                </h3>
                <p>
                  {t('practice.on_demand_practice.conversation.instructions')}
                </p>
              </Card.Content>
            </Card>
          </div>
        )}
        {loadError && (
          <div className="fz-24 m-a ta-c">
            {t('practice.on_demand_practice.error.scenario_load')}
          </div>
        )}
        {!loading && !loadError && !isConversation && (
          <>
            <h1 className="ff-ss fz-24 fw-b mb-16 text-wrap lh-1">
              {scenario.title}
            </h1>
          </>
        )}
        {loading && <Spinner />}
        {!loading && !loadError && isConversation && (
          <LearnerConversation
            title={scenario.title}
            prompt={scenario.prompt}
            conversation={scenario.conversation}
            saveAttempt={this.saveAttemptWithResponses}
            showAttempt={this.showLatestAttempt}
            saveError={saveError}
            saving={saving}
            isPreviewing={this.props.isPreviewing}
            isPractice
          />
        )}
        {!loading && !loadError && !isConversation && (
          <>
            {(scenario.sanitizedExampleRecordingHTML ||
              scenario.feedbackCriteria.length > 0) && (
              <QuestionPrompt
                criteria={scenario.feedbackCriteria}
                exampleRecordingHtml={scenario.sanitizedExampleRecordingHTML}
                apiKey={scenario.ziggeoApiKey}
                url={scenario.playbackUrl}
                hasZiggeoForAllMedia={this.props.hasZiggeoForAllMedia}
                odpQuestionPrompt
              />
            )}
            <QuestionPanel
              saveAttempt={this.saveAttempt}
              saveAttemptWithFeedback={this.saveAttemptWithFeedback}
              scenario={scenario}
              answer={answer}
              isComplete={isComplete}
              saving={saving}
            >
              {this.renderQuestion()}
            </QuestionPanel>
          </>
        )}
        {saveError && (
          <AlertBox onDismiss={this.closeSaveErrorModal}>
            <Heading>
              {t('practice.on_demand_practice.error.save_attempt.title')}
            </Heading>
            {t('practice.on_demand_practice.error.save_attempt.description')}
            <ButtonGroup alignEnd className="mt-8">
              <Button customColor onClick={this.closeSaveErrorModal}>
                {t('common.ok')}
              </Button>
            </ButtonGroup>
          </AlertBox>
        )}
      </div>
    );
  }
}

export default CurrentResponseTab;
